/**
 * @file useTasksPage.ts
 * @description Данные страницы «Задачи» (phase19.31, SC60; crm.md T7):
 * отбор из адреса → `GET /admin/crm/activities?kinds&status&mine|overdue|
 * unassigned&counts=true` → строки, всего и числа пилюль.
 *
 * Перечитывание тихое: прежний список остаётся на экране, пока идёт новый
 * запрос; гонку ответов гасит номер запроса. После любого действия список
 * перечитывается с сервера, а не правится руками (один источник правды).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { listActivities, type ActivityDto, type TaskCountsDto } from "../../../lib/api-crm";
import { parseTasksQuery, toListTasksParams, writeTasksQuery, type TasksQuery } from "./tasks-query";

export const TASKS_ERROR_TEXT = {
  empty: "Не удалось загрузить задачи. Проверьте связь и попробуйте ещё раз",
  stale: "Не удалось обновить список, данные могут быть устаревшими",
} as const;

export interface TasksPageState {
  query: TasksQuery;
  setQuery: (patch: Partial<TasksQuery>) => void;
  items: ActivityDto[];
  total: number;
  counts: TaskCountsDto | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTasksPage(): TasksPageState {
  const [params, setParams] = useSearchParams();
  // Ключ — нормализованный адрес: мусор в адресе не перезапускает запрос.
  const key = writeTasksQuery(parseTasksQuery(params)).toString();
  const query = parseTasksQuery(new URLSearchParams(key));
  const [items, setItems] = useState<ActivityDto[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TaskCountsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    listActivities(toListTasksParams(parseTasksQuery(new URLSearchParams(key))))
      .then((res) => {
        if (id !== requestId.current) return;
        setItems(res.items);
        setTotal(res.total);
        setCounts(res.counts ?? null);
        setFailed(false);
      })
      .catch(() => {
        if (id === requestId.current) setFailed(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [key, attempt]);

  const setQuery = useCallback(
    (patch: Partial<TasksQuery>) => {
      setParams((prev) => writeTasksQuery({ ...parseTasksQuery(prev), ...patch }), { replace: true });
    },
    [setParams],
  );

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    query,
    setQuery,
    items,
    total,
    counts,
    loading,
    error: failed ? (items.length > 0 ? TASKS_ERROR_TEXT.stale : TASKS_ERROR_TEXT.empty) : null,
    refresh,
  };
}
